import { DoctoraliaIntegration } from "@/types/DoctoraliaIntegration";

export function camelCaseToSnakeCase(input: string): string {
  return input.replace(/([A-Z])/g, "_$1").toLowerCase();
}
export function camelCaseToKebabCase(input: string): string {
  return input
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
    .toLowerCase();
}

export async function fetchInternalApi(
  method: string,
  url: string,
  headers = {}
): Promise<Response> {
  const token = sessionStorage.getItem("tk") ?? localStorage.getItem("tk");
  const requestHeaders = {
    ...(token ? { "x-access-token": token } : {}),
    ...headers,
  };
  return await fetch(url, { method, headers: requestHeaders });
}

/**
 * Return a boolean indicating if the current user is an admin
 * @todo: after full migrate to SPA version, use session data instead of document.userProperties
 */
export function isCurrentUserAdmin() {
  return document.userProperties?.admin === "Sim";
}
/**
 * Get the current user license ID
 * @todo: after full migrate to SPA version, use session data instead of document.userProperties
 */
export function getCurrentLicenseId() {
  return document.userProperties?.licencaID
    ? parseInt(`${document.userProperties.licencaID}`)
    : null;
}

/**
 * Get the current user ID in the table
 * @todo: after full migrate to SPA version, use session data instead of document.userProperties
 */
export function getCurrentUserIdInTable() {
  return document.userProperties?.idInTable
    ? parseInt(`${document.userProperties.idInTable}`)
    : null;
}

/**
 * Get the current feegow Doctor internal ID
 * @todo: after full migrate to SPA version, use session data instead of document.userProperties
 */
export function getCurrentProfessionalId() {
  return document.userProperties?.tipoUsuario === "profissionais" &&
    document.userProperties?.idInTable
    ? parseInt(`${document.userProperties.idInTable}`)
    : null;
}

export function getCurrentUnitId() {
  return document.userProperties?.unidadeID
    ? parseInt(document.userProperties.unidadeID as string)
    : 0;
}

export function getCurrentUserId() {
  return document.userProperties?.usuarioID
    ? parseInt(document.userProperties.usuarioID as string)
    : 0;
}

export function getUserType(integrationData: DoctoraliaIntegration) {
  const clinic = integrationData.clinic;

  if (!clinic) {
    return null;
  }

  const feegowDoctors = clinic.feegowDoctors;
  const isIntegrationIndividual = feegowDoctors.length === 1;

  if (isCurrentUserAdmin() && !isIntegrationIndividual) {
    return "facility_admin";
  }

  if (isIntegrationIndividual) {
    return "individual_doctor";
  }

  return "facility_doctor";
}

export function getDPWidgetUrl(
  module: string,
  professionalId?: string | number | null,
  otherParams?: { [key: string]: string }
): string {
  // devParam is used to enable the dev mode in the microapp
  let devParam = "";
  if (
    ["dev", "homolog", "localhost"].some(function (p) {
      return window.location.hostname.includes(p);
    })
  ) {
    devParam = "&dev=true";
  }

  // hashParams is used to pass parameters to the react app router

  // Preserve existing hash if not overridden
  const currentHash = window.location.hash;
  let baseHash = currentHash !== "" ? currentHash.split("?")[0] : ""; // Separates the hash base, excluding existing parameters

  let hashParamsString = "";
  let hashParams: { [key: string]: string } = {};
  if (professionalId) {
    hashParams["id"] = `${professionalId}`;
  }
  if (otherParams && Object.keys(otherParams).length > 0) {
    hashParams = { ...hashParams, ...otherParams };
  }
  if (Object.keys(hashParams).length > 0) {
    hashParamsString =
      "?" +
      Object.keys(hashParams)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(hashParams[key])}`
        )
        .join("&");
    baseHash = baseHash || "#"; //If there was no baseHash, initialize with '#'
  }

  return `?P=microapp&Pers=1&app=doctoralia-integration&module=${module}${devParam}${baseHash}${hashParamsString}`;
}

/**
 * Asserts if a given string is a valid JWT token withing Feegow's TK context
 * @param tk A Feegow TK JWT token
 * @returns A boolean indicating if the token is valid
 */
export function isValidJWT(tk: string | null): boolean {
  if (!tk) {
    return false;
  }

  const sections = tk.split(".");
  if (sections.length !== 3) {
    return false;
  }

  const payload = sections[1];

  try {
    const decodedPayload = atob(payload);
    const parsedPayload = JSON.parse(decodedPayload);

    return parsedPayload.success === true && !!parsedPayload.licenseId;
  } catch (e) {
    return false;
  }
}
